/* ─────────────────────────────────────────────
   SHARED UTILITY HELPERS
   ───────────────────────────────────────────── */

/**
 * Generate a unique ID
 */
export const uid = () => crypto.randomUUID();

/**
 * Truncate text to N words with ellipsis
 */
export const truncateWords = (text, count = 5) => {
  const words = text.trim().split(/\s+/);
  if (words.length <= count) return text;
  return words.slice(0, count).join(" ") + "…";
};

/**
 * Format timestamp to readable time
 */
export const formatTime = (ts) =>
    new Date(ts).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

/**
 * Format Indian-style price (Lakhs / Crores)
 */
export const formatPrice = (price) => {
  if (!price) return "Price on request";

  const cleaned = String(price).replace(/[₹,]/g, "");
  const num = Number(cleaned);

  if (isNaN(num)) return price;

  if (num >= 10000000) {
    return `₹ ${(num / 10000000).toFixed(2)} Cr`;
  }

  if (num >= 100000) {
    return `₹ ${(num / 100000).toFixed(2)} L`;
  }

  return `₹ ${num.toLocaleString("en-IN")}`;
};

/**
 * Debounce function
 */
export const debounce = (fn, ms = 300) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
};

/**
 * Markdown formatter — supports headings, bold, italic,
 * bullet lists, numbered lists, code blocks, inline code
 */
export const parseMarkdown = (text) => {
  if (!text) return "";

  // Step 1 — protect code blocks before escaping HTML
  const codeBlocks = [];
  text = text.replace(/```[\w]*\n?([\s\S]*?)```/g, (_, code) => {
    const idx = codeBlocks.length;
    codeBlocks.push(code.trim());
    return `%%CODE_BLOCK_${idx}%%`;
  });

  const inlineCodes = [];
  text = text.replace(/`([^`]+)`/g, (_, code) => {
    const idx = inlineCodes.length;
    inlineCodes.push(code);
    return `%%INLINE_CODE_${idx}%%`;
  });

  // Step 2 — escape HTML
  text = text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  // Step 3 — headings (#### → h4, ### → h3, ## → h2, # → h2)
  text = text.replace(/^####\s+(.+)$/gm, '<h4 class="ai-h4">$1</h4>');
  text = text.replace(/^###\s+(.+)$/gm,  '<h3 class="ai-h3">$1</h3>');
  text = text.replace(/^##\s+(.+)$/gm,   '<h3 class="ai-h3">$1</h3>');
  text = text.replace(/^#\s+(.+)$/gm,    '<h3 class="ai-h3">$1</h3>');

  // Step 4 — bold + italic
  text = text.replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>");
  text = text.replace(/\*\*(.+?)\*\*/g,       "<strong>$1</strong>");
  text = text.replace(/\*([^*\n]+?)\*/g,        "<em>$1</em>");

  // Step 5 — bullet lists (lines starting with - or *)
  text = text.replace(/^([\-\*])\s+(.+)$/gm, "<li>$2</li>");
  text = text.replace(/(<li>.*<\/li>)/gs, (match) => {
    // Only wrap consecutive <li> in <ul> if not already wrapped
    return '<ul class="ai-list">' + match + '</ul>';
  });

  // Step 6 — numbered lists
  text = text.replace(/^\d+\.\s+(.+)$/gm, "<li>$1</li>");

  // Step 7 — horizontal rule
  text = text.replace(/^---$/gm, "<hr/>");

  // Step 8 — line breaks (skip lines that are block elements)
  text = text.replace(/\n{2,}/g, "<br/><br/>");
  text = text.replace(/\n/g, "<br/>");

  // Step 9 — restore code blocks
  text = text.replace(/%%CODE_BLOCK_(\d+)%%/g, (_, i) =>
      '<pre class="code-block"><code>' + codeBlocks[parseInt(i)] + '</code></pre>'
  );
  text = text.replace(/%%INLINE_CODE_(\d+)%%/g, (_, i) =>
      '<code class="inline-code">' + inlineCodes[parseInt(i)] + '</code>'
  );

  return text;
};

/**
 * Keyboard shortcut helper
 */
export const isShortcut = (e, key, mod = "ctrl") => {
  const modKey = mod === "ctrl" ? e.ctrlKey || e.metaKey : e[`${mod}Key`];
  return modKey && e.key.toLowerCase() === key.toLowerCase();
};